/*
 * Hexlox Product Finder — screens.
 *
 * Draws the pinned bar, the Finder dialog and the help pop-ups. All product logic lives in
 * hexlox-finder-engine.js; this file only turns the engine's state into HTML and user taps into
 * engine actions. Data: hexlox-finder-config.json (questions, products, images) and
 * hexlox-finder-texts.json (EN/DE texts from the master sheet).
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-hxf-root]');
  if (!root || root.hxfReady || !window.HexloxFinderEngine) return;
  root.hxfReady = true;
  if (root.getAttribute('data-hidden') === 'true') return;

  var lang = root.getAttribute('data-lang') === 'de' ? 'de' : 'en';
  var routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var config, texts, engine, state;
  var analytics = { track: function () {} };   // replaced at start-up if hexlox-finder-analytics.js loaded
  var products = {};       // handle -> product JSON from /products/<handle>.js (titles in the shop's language)
  var productLoads = {};   // handle -> Promise while/after loading
  var cartBusy = false;    // an add-to-cart is running (blocks double taps)
  var cartNotice = null;   // message screen after adding: { kind: 'unavailable' | 'partial' | 'failed', ... }
  var lastScreen = null;
  var els = {};

  // ---- Helpers ---------------------------------------------------------------------------
  var hasOwn = function (o, k) { return Object.prototype.hasOwnProperty.call(o, k); };

  function esc(value) {
    return String(value === undefined || value === null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function plain(html) {
    var d = document.createElement('div');
    d.innerHTML = html;
    return d.textContent;
  }

  function storage() {
    try { return window.sessionStorage; } catch (e) { return null; }
  }

  // ---- Texts -----------------------------------------------------------------------------
  // A text reference is a sheet ID ("DE-016"), {literal: "..."}, or {text: ID, vars: {...}}.
  // Sheet texts may contain <strong>, <a>, <br>; everything filled into {placeholders} is escaped,
  // except values passed as {html: "..."} that this file built itself.
  function template(id) {
    var t = texts[lang] && hasOwn(texts[lang], id) ? texts[lang][id] : texts.en[id];
    if (t === undefined) {
      if (window.console) console.warn('Hexlox Finder: missing text ' + id);
      return '';
    }
    return t;
  }

  function fmt(ref, vars) {
    if (ref === null || ref === undefined) return '';
    if (typeof ref === 'object' && hasOwn(ref, 'html')) return ref.html;
    if (typeof ref === 'object' && hasOwn(ref, 'literal')) return esc(ref.literal);
    if (typeof ref === 'object') return fmt(ref.text, Object.assign({}, ref.vars, vars));
    vars = vars || {};
    var t = template(ref);
    // {n:one|other} — singular/plural by the number in vars.n
    t = t.replace(/\{(\w+):([^|{}]*)\|([^{}]*)\}/g, function (_, name, one, other) {
      return Number(vars[name]) === 1 ? one : other;
    });
    return t.replace(/\{([^{}]+)\}/g, function (whole, name) {
      if (!hasOwn(vars, name)) return whole;
      var v = vars[name];
      if (v && typeof v === 'object') return fmt(v);
      if (typeof v === 'number' && lang === 'de') v = String(v).replace('.', ',');
      return esc(v);
    });
  }

  function txt(ref, vars) { return plain(fmt(ref, vars)); }

  // Sheet texts mark links as bare <a>…</a>; the config lists where each one goes, in order.
  function withLinks(html, links) {
    var i = 0;
    return html.replace(/<a>/g, function () {
      var path = links && links[i++];
      return path ? '<a href="' + esc(routesRoot + path) + '" target="_blank" rel="noopener">' : '<a>';
    });
  }

  // ---- Images (Shopify Files CDN, resized by the CDN, lazy-loaded) ------------------------
  function sized(url, w) { return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w; }

  function img(key, alt, cls, sizes, eager) {
    var image = config.images[key];
    if (!image) return '';
    var u = image.url;
    return '<img class="' + cls + '" src="' + esc(sized(u, 800)) + '" srcset="' +
      [300, 500, 800, 1200].map(function (w) { return esc(sized(u, w)) + ' ' + w + 'w'; }).join(', ') +
      '" sizes="' + (sizes || '(max-width: 700px) 50vw, 300px') + '" alt="' + esc(alt) + '"' +
      (eager ? '' : ' loading="lazy"') + ' decoding="async">';
  }

  // ---- State -----------------------------------------------------------------------------
  // The selection survives page changes within the visit (sessionStorage), so the bar keeps showing it.
  function storeKey() { return 'hxf-state:' + config.finder_version; }

  function save() {
    var s = storage();
    if (!s) return;
    try { s.setItem(storeKey(), JSON.stringify(state)); } catch (e) { /* storage full or blocked */ }
  }

  function load() {
    var s = storage();
    var saved = null;
    try { saved = s && JSON.parse(s.getItem(storeKey()) || 'null'); } catch (e) { saved = null; }
    if (!saved || typeof saved !== 'object') return engine.initialState();
    try {
      var restored = Object.assign(engine.initialState(), saved);
      engine.selection(restored);              // throws if the saved answers no longer fit the config
      if (restored.view === 'question') engine.currentQuestionId(restored);
      return restored;
    } catch (e) {
      return engine.initialState();
    }
  }

  function act(action, opts) {
    var before = state;
    state = engine.dispatch(state, action);
    save();
    trackAction(action, before, state);
    if (!opts || opts.render !== false) render();
  }

  // ---- Analytics (only with the visitor's consent; see hexlox-finder-analytics.js) --------
  // Stable IDs only (part, question and answer IDs from the config); never typed values or the Hexlox Code.
  function trackAction(action, before, after) {
    try {
      if (action.type === 'togglePart' && before.selected.indexOf(action.part) === -1 && after.selected.indexOf(action.part) !== -1) {
        analytics.track('part_selected', { finder_part: action.part });
      }
      var qid = before.view === 'question' ? engine.currentQuestionId(before) : null;
      var moved = after.view !== before.view || after.current !== before.current ||
        (after.view === 'question' && engine.currentQuestionId(after) !== qid);
      if (action.type === 'answer' && qid === action.question) {
        analytics.track('question_answered', { finder_part: before.current, finder_question: qid, finder_answer: action.value });
      } else if (action.type === 'submit' && qid && moved) {
        var type = config.questions[qid].type;
        analytics.track('question_answered', { finder_part: before.current, finder_question: qid, finder_answer: type === 'measurements' ? 'entered' : 'submitted' });
      }
      if (after.view === 'selection' && before.view !== 'selection') {
        var sum = engine.summary(after);
        analytics.track('finder_complete', { finder_parts: sum.parts, finder_products: sum.products });
      }
    } catch (e) { /* analytics must never break the Finder */ }
  }

  function trackCart(result, sum) {
    try {
      var lines = function (list) { return (list || []).length; };
      if (result.status === 'ok' || result.status === 'partial') {
        analytics.track('add_to_cart_success', { finder_parts: sum.parts, finder_products: sum.products });
      }
      if (result.status !== 'ok') {
        var reason = result.status === 'partial' && !lines(result.failed) ? 'code_not_saved' : result.status;
        analytics.track('add_to_cart_error', {
          finder_error: reason,
          finder_products: lines(result.status === 'unavailable' ? result.unavailable : result.failed)
        });
      }
    } catch (e) { /* analytics must never break the Finder */ }
  }

  // ---- Products (titles in the shop's language, from Shopify) ----------------------------
  function productInfo(key) {
    var p = config.products[key];
    var data = products[p.handle];
    if (!data || data === 'loading') return null;
    if (data === 'error') return { title: p.name, variant: '' };
    var variant = (data.variants || []).filter(function (v) { return Number(v.id) === Number(p.variant_id); })[0];
    var vt = variant && variant.title && !/^Default( Title)?$/i.test(variant.title) && p.show_variant_title !== false ? variant.title : '';
    return { title: data.title, variant: vt };
  }

  // Resolves when every product is loaded (or failed to load). Re-renders "Your selection" once.
  function loadProducts(keys) {
    var handles = keys.map(function (k) { return config.products[k].handle; });
    var fresh = handles.filter(function (h, i) { return handles.indexOf(h) === i && !hasOwn(productLoads, h); });
    fresh.forEach(function (h) {
      products[h] = 'loading';
      productLoads[h] = fetch(routesRoot + 'products/' + encodeURIComponent(h) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function (json) { products[h] = json; })
        .catch(function () { products[h] = 'error'; });
    });
    var all = Promise.all(handles.map(function (h) { return productLoads[h]; }));
    if (fresh.length) all.then(function () { if (state.view === 'selection' && !cartNotice) render(); });
    return all;
  }

  function keyForVariant(variantId) {
    return Object.keys(config.products).filter(function (k) { return Number(config.products[k].variant_id) === Number(variantId); })[0];
  }

  // Stock as Shopify reported it when the product was loaded: true / false, or undefined if unknown.
  function variantAvailable(variantId) {
    var key = keyForVariant(variantId);
    var data = key && products[config.products[key].handle];
    if (!data || typeof data !== 'object') return undefined;
    var v = (data.variants || []).filter(function (x) { return Number(x.id) === Number(variantId); })[0];
    return v && typeof v.available === 'boolean' ? v.available : undefined;
  }

  function productName(key) {
    var info = productInfo(key);
    if (!info) return '<span class="hxf-skeleton" aria-hidden="true"></span>';
    return esc(info.title) + (info.variant ? '<span class="hxf-variant">' + esc(info.variant) + '</span>' : '');
  }

  // ---- Help pop-ups ----------------------------------------------------------------------
  function blocksHtml(blocks) {
    return (blocks || []).map(function (b) {
      if (b.image) return img(b.image, txt(b.alt), 'hxf-help-image', '(max-width: 700px) 100vw, 760px');
      if (b.p) return '<p>' + withLinks(fmt(b.p), b.links) + '</p>';
      if (b.html) return '<div class="hxf-help-html">' + withLinks(fmt(b.html), b.links) + '</div>';
      if (b.h3) return '<h3>' + fmt(b.h3) + '</h3>';
      if (b.caption) return '<div class="hxf-caption">' + fmt(b.caption) + '</div>';
      if (b.ul) return '<ul>' + b.ul.map(function (li) { return '<li>' + fmt(li) + '</li>'; }).join('') + '</ul>';
      if (b.table) {
        return '<div class="hxf-table-wrap"><table class="hxf-table"><thead><tr>' +
          b.table.head.map(function (h) { return '<th>' + fmt(h) + '</th>'; }).join('') + '</tr></thead><tbody>' +
          b.table.rows.map(function (r) {
            return '<tr>' + r.map(function (c) { return '<td>' + fmt(c) + '</td>'; }).join('') + '</tr>';
          }).join('') + '</tbody></table></div>';
      }
      return '';
    }).join('');
  }

  function helpButton(key) {
    var h = config.help[key];
    if (!h || !h.title) return '';
    return '<button type="button" class="hxf-help-btn" data-act="help" data-help="' + esc(key) + '" aria-haspopup="dialog">' +
      '<span>' + fmt(h.title) + '</span></button>';
  }

  function openHelp(key) {
    var h = config.help[key];
    if (!h) return;
    els.helpTitle.innerHTML = fmt(h.title);
    els.helpBody.innerHTML = blocksHtml(h.blocks);
    els.helpBody.scrollTop = 0;
    if (!els.help.open) els.help.showModal();
    els.helpClose.focus();
  }

  // ---- Screens ---------------------------------------------------------------------------
  function partName(id) {
    return fmt(config.parts.filter(function (p) { return p.id === id; })[0].name);
  }

  function partById(id) { return config.parts.filter(function (p) { return p.id === id; })[0]; }

  function actionStack(buttons) {
    return '<div class="hxf-action-stack">' + buttons.join('') + escapeButton() + '</div>';
  }

  function escapeButton() {
    return '<button type="button" class="hxf-escape" data-act="restart">' + fmt('DE-012') + '</button>';
  }

  function heading(html) { return '<h2 class="hxf-h2" tabindex="-1">' + html + '</h2>'; }

  function renderParts() {
    var tiles = config.parts.map(function (p) {
      var done = state.completed.indexOf(p.id) !== -1;
      var selected = state.selected.indexOf(p.id) !== -1;
      return '<button type="button" class="hxf-part' + (selected ? ' is-selected' : '') + (done ? ' is-done' : '') + '"' +
        ' data-act="togglePart" data-part="' + p.id + '" aria-pressed="' + selected + '"' + (done ? ' disabled' : '') + '>' +
        img(p.image, '', 'hxf-part-image', '(max-width: 700px) 45vw, 300px', true) +
        '<span class="hxf-part-name">' + fmt(p.name) + '</span>' +
        '<span class="hxf-part-state">' + (done ? '✓' : fmt('DE-011')) + '</span></button>';
    }).join('');
    var n = engine.continueCount(state);
    var buttons = [];
    if (n) buttons.push('<button type="button" class="hxf-primary" data-act="start">' + fmt('DE-013', { n: n }) + '</button>');
    if (state.completed.length) buttons.push('<button type="button" class="hxf-secondary" data-act="showSelection">' + fmt('DE-102') + '</button>');
    return heading(fmt('DE-004')) + '<p class="hxf-lead">' + fmt('DE-005') + '</p>' +
      '<div class="hxf-part-grid">' + tiles + '</div>' +
      (buttons.length ? actionStack(buttons) : escapeButton());
  }

  // Longest number of questions still to come from qid (including qid), for the progress dots.
  function depth(qid) {
    var q = config.questions[qid];
    var rest = 0;
    (q.options || []).forEach(function (o) { if (o.next) rest = Math.max(rest, depth(o.next)); });
    return 1 + rest;
  }

  function questionHeader(part, answered, qid) {
    var total = answered + depth(qid);
    var dots = '';
    for (var i = 0; i < total; i++) dots += '<span class="hxf-dot' + (i <= answered ? ' is-active' : '') + '"></span>';
    return '<div class="hxf-page-actions"><button type="button" class="hxf-back" data-act="back">' + fmt('DE-015') + '</button>' + escapeButton() + '</div>' +
      '<div class="hxf-eyebrow">' + img(partById(part).image, '', 'hxf-thumb', '44px', true) + '<span>' + partName(part) + '</span></div>' +
      '<div class="hxf-progress" aria-hidden="true">' + dots + '</div>';
  }

  function renderChoice(qid, q) {
    var visual = q.layout === 'visual';
    var opts = q.options.map(function (o) {
      var label = fmt(o.label);
      var image = o.image ? (visual
        ? '<span class="hxf-visual-art">' + img(o.image, plain(label), 'hxf-visual-img', '(max-width: 700px) 45vw, 320px', true) + '</span>'
        : img(o.image, plain(label), 'hxf-option-image', '(max-width: 700px) 45vw, 480px', true)) : '';
      return '<button type="button" class="' + (visual ? 'hxf-visual' : 'hxf-option') + '" data-act="answer" data-q="' + esc(qid) + '" data-v="' + esc(o.id) + '">' +
        image + '<strong>' + label + '</strong>' + (o.text ? '<span>' + fmt(o.text) + '</span>' : '') + '</button>';
    }).join('');
    var helps = (q.help || []).map(helpButton).join('');
    return '<div class="' + (visual ? 'hxf-visual-grid' : 'hxf-option-grid') + '">' + opts + '</div>' +
      (helps ? '<div class="hxf-help-stack">' + helps + '</div>' : '');
  }

  function exampleFor(field) { return lang === 'de' ? field.example.replace('.', ',') : field.example; }

  function measureCheck(part, field, value) {
    var c = engine.checkMeasurement(part, field.id, value);
    if (!c.message) return { status: c.status, html: '' };
    var vars = Object.assign({}, c.message.vars);
    if (hasOwn(vars, 'example')) vars.example = exampleFor(field);
    return { status: c.status, html: fmt(c.message.text, vars) };
  }

  function renderMeasurements(part, qid, q) {
    var draft = state.drafts[qid] || {};
    var fields = config.measurements.fields;
    var html = fields.map(function (f, i) {
      var id = 'hxf-mf-' + f.id;
      return '<div class="hxf-measure-field">' + helpButton(f.help) +
        '<label class="hxf-measure-label" for="' + id + '">' + fmt(f.label) + '</label>' +
        (f.sub ? '<div class="hxf-measure-help" id="' + id + '-sub">' + fmt(f.sub) + '</div>' : '') +
        '<div class="hxf-measure-input-wrap"><input class="hxf-measure-input" id="' + id + '" type="text" inputmode="decimal"' +
        ' autocomplete="off" autocapitalize="off" spellcheck="false" maxlength="' + config.measurements.max_length + '"' +
        ' enterkeyhint="' + (i === fields.length - 1 ? 'done' : 'next') + '" placeholder="' + esc(txt(f.placeholder)) + '"' +
        ' value="' + esc(draft[f.id] || '') + '" data-field="' + f.id + '" aria-describedby="' + id + '-hint' + (f.sub ? ' ' + id + '-sub' : '') + '">' +
        '<span class="hxf-measure-unit" aria-hidden="true">mm</span></div>' +
        '<p class="hxf-measure-hint" id="' + id + '-hint" aria-live="polite"></p></div>';
    }).join('');
    return '<div class="hxf-measure-fields">' + html + '</div>' +
      '<button type="button" class="hxf-primary" data-act="submit" data-measure-continue disabled>' + fmt('DE-014') + '</button>';
  }

  // Updates hints and the Continue button in place, so typing never loses focus.
  function refreshMeasurements() {
    var part = state.current;
    var draft = state.drafts[engine.currentQuestionId(state)] || {};
    var allOk = true;
    config.measurements.fields.forEach(function (f) {
      var input = els.body.querySelector('#hxf-mf-' + f.id);
      var hint = els.body.querySelector('#hxf-mf-' + f.id + '-hint');
      if (!input || !hint) return;
      var c = measureCheck(part, f, draft[f.id]);
      // Errors show once the customer has left the field or pressed Continue; warnings show right away.
      var showError = c.status === 'error' && input.getAttribute('data-touched') === 'true';
      hint.innerHTML = c.status === 'warning' || showError ? c.html : '';
      hint.className = 'hxf-measure-hint' + (c.status === 'error' ? ' is-error' : '');
      input.classList.toggle('is-invalid', showError);
      input.setAttribute('aria-invalid', showError ? 'true' : 'false');
      if (c.status !== 'ok' && c.status !== 'warning') allOk = false;
    });
    var btn = els.body.querySelector('[data-measure-continue]');
    if (btn) btn.disabled = !allOk;
  }

  function renderGrid(qid, q) {
    var draft = state.drafts[qid] || {};
    var total = 0;
    var rows = q.rows.map(function (r) {
      var qty = Number(draft[r.id]) || 0;
      total += qty;
      var label = r.size + ' mm';
      var aria = txt('DE-150', { size: r.size }) + (r.non_magnetic ? ' ' + txt('DE-146') : '');
      return '<div class="hxf-insert-row' + (r.non_magnetic ? ' is-nonmag' : '') + '">' +
        '<div class="hxf-insert-size">' + label + (r.non_magnetic ? '<span class="hxf-nonmag-label">' + fmt('DE-146') + '</span>' : '') + '</div>' +
        '<div class="hxf-qty-controls" role="group" aria-label="' + esc(aria) + '">' +
        '<button type="button" class="hxf-qty-btn" data-act="gridQty" data-row="' + esc(r.id) + '" data-delta="-1"' + (qty ? '' : ' disabled') + ' aria-label="−">−</button>' +
        '<span class="hxf-qty-value" aria-live="polite">' + qty + '</span>' +
        '<button type="button" class="hxf-qty-btn" data-act="gridQty" data-row="' + esc(r.id) + '" data-delta="1" aria-label="+">+</button></div>' +
        '<div class="hxf-insert-usage">' + fmt(r.usage) + '</div></div>';
    }).join('');
    var intro = q.intro ? '<div class="hxf-intro">' + blocksHtml(config.help[q.intro].blocks) + '</div>' : '';
    return intro + '<div class="hxf-insert-grid">' + rows + '</div>' +
      '<div class="hxf-grid-total">' + fmt(q.total, { n: total }) + '</div>' +
      '<button type="button" class="hxf-primary" data-act="submit"' + (total ? '' : ' disabled') + '>' + fmt('DE-014') + '</button>' +
      '<div class="hxf-help-stack">' + (q.help || []).map(helpButton).join('') + '</div>';
  }

  function renderQuestion() {
    var part = state.current;
    var qid = engine.currentQuestionId(state);
    var q = config.questions[qid];
    var answered = engine.path(part, state.answers[part]).answered.length;
    var body = q.type === 'measurements' ? renderMeasurements(part, qid, q)
      : q.type === 'grid' ? renderGrid(qid, q) : renderChoice(qid, q);
    return questionHeader(part, answered, qid) + heading(fmt(q.title)) + body;
  }

  function renderTransition() {
    var next = state.queue[0];
    return '<div class="hxf-eyebrow hxf-done">' + fmt('DE-098', { part: { html: partName(state.finished) } }) + '</div>' +
      heading(fmt('DE-099', { part: { html: partName(state.finished) } })) +
      '<p class="hxf-lead">' + fmt('DE-100', { part: { html: partName(next) } }) + '</p>' +
      '<button type="button" class="hxf-primary" data-act="continue">' + fmt('DE-014') + '</button>' + escapeButton();
  }

  function lineHtml(r, l) {
    var check = '';
    if (l.check) check = fmt(l.check);
    else if (l.reasons && l.reasons.length) {
      check = fmt('DE-172', { reasons: { html: l.reasons.map(function (x) { return fmt(x); }).join('. ') } });
    }
    return '<div class="hxf-rec-item">' +
      '<div class="hxf-reco-title">' + productName(l.product) + '</div>' +
      (check ? '<div class="hxf-check">' + check + '</div>' : '') +
      (l.detail ? '<div class="hxf-small">' + fmt(l.detail) + '</div>' : '') +
      '<div class="hxf-qty"><strong>' + fmt('DE-154') + '</strong><div class="hxf-qty-controls">' +
      '<button type="button" class="hxf-qty-btn" data-act="changeQty" data-part="' + r.part + '" data-line="' + esc(l.key) + '" data-delta="-1"' + (l.quantity > 1 ? '' : ' disabled') + ' aria-label="−">−</button>' +
      '<strong class="hxf-qty-value" aria-live="polite">' + l.quantity + '</strong>' +
      '<button type="button" class="hxf-qty-btn" data-act="changeQty" data-part="' + r.part + '" data-line="' + esc(l.key) + '" data-delta="1" aria-label="+">+</button></div></div>' +
      (l.note ? '<div class="hxf-small hxf-note">' + fmt(l.note) + '</div>' : '') +
      '</div>';
  }

  function addonHtml(r, a) {
    var id = 'hxf-own-insert-' + r.part;
    var title = a.include ? fmt('DE-155', { size: a.size }) : productName(a.product);
    var per = r.axle === 'nut' ? { literal: 'Hexnut' } : { text: 'X-axle' };
    return '<div class="hxf-addon"><div class="hxf-selection-row"><strong>' + title + '</strong><strong>' +
      (a.include ? a.quantity + ' ×' : fmt('DE-158')) + '</strong></div>' +
      '<div class="hxf-small">' + fmt('DE-156', { axle: a.axleLabel, size: a.size, per: per }) + '</div>' +
      '<div class="hxf-own"><input type="checkbox" id="' + id + '" data-act="ownInsert" data-part="' + r.part + '"' + (a.include ? '' : ' checked') + '>' +
      '<label for="' + id + '">' + fmt('DE-157', { size: a.size, qty: a.quantity }) + '</label></div></div>';
  }

  function renderSelection() {
    var sel = engine.selection(state);
    var keys = [config.key_addon.product];
    var sections = sel.map(function (r) {
      r.lines.forEach(function (l) { keys.push(l.product); });
      r.addons.forEach(function (a) { keys.push(a.product); });
      return '<section class="hxf-reco hxf-part-section">' +
        '<div class="hxf-eyebrow">' + img(partById(r.part).image, '', 'hxf-thumb', '44px', true) + '<span>' + partName(r.part) + '</span></div>' +
        r.lines.map(function (l) { return lineHtml(r, l); }).join('') +
        r.addons.map(function (a) { return addonHtml(r, a); }).join('') + '</section>';
    }).join('');
    loadProducts(keys);
    var key = config.key_addon;
    var keyCard = '<div class="hxf-reco"><div class="hxf-selection-row"><strong>' +
      (state.ownKey ? productName(key.product) : fmt(key.title)) + '</strong><strong>' +
      (state.ownKey ? fmt('DE-158') : key.quantity + ' ×') + '</strong></div>' +
      '<div class="hxf-small">' + fmt(key.text) + '</div>' +
      '<div class="hxf-own"><input type="checkbox" id="hxf-own-key" data-act="ownKey"' + (state.ownKey ? ' checked' : '') + '>' +
      '<label for="hxf-own-key">' + fmt(key.opt_out) + '</label></div></div>';
    var code = config.hexlox_code;
    var codeCard = '<div class="hxf-reco hxf-code-card"><label class="hxf-selection-row" for="hxf-code"><strong>' + fmt(code.label) + '</strong></label>' +
      (code.help || []).map(helpButton).join('') +
      '<input class="hxf-code-input" id="hxf-code" type="text" inputmode="text" autocomplete="off" autocapitalize="characters" spellcheck="false"' +
      ' maxlength="' + code.max_length + '" placeholder="' + esc(txt(code.placeholder)) + '" value="' + esc(state.hexloxCode) + '" data-code></div>';
    return heading(fmt('DE-102')) + sections + keyCard + codeCard +
      actionStack([
        '<button type="button" class="hxf-primary" data-act="addToCart"' + (cartBusy ? ' disabled aria-busy="true"' : '') + '>' +
          (cartBusy ? fmt('X-cart-adding') : fmt('DE-169')) + '</button>',
        '<button type="button" class="hxf-secondary" data-act="addAnother">' + fmt('DE-170') + '</button>'
      ]);
  }

  // ---- Messages after adding to the cart -----------------------------------------------
  function noticeList(items) {
    return '<ul class="hxf-notice-list">' + items.map(function (item) {
      var key = keyForVariant(item.id);
      var name = key ? productName(key) : esc(item.id);
      var part = item.properties && item.properties._finder_part ? ' <span class="hxf-small">(' + partName(item.properties._finder_part) + ')</span>' : '';
      return '<li><strong>' + name + '</strong>' + (item.quantity > 1 ? ' · ' + item.quantity + ' ×' : '') + part + '</li>';
    }).join('') + '</ul>';
  }

  function renderNotice() {
    var n = cartNotice;
    var stack = function (buttons) { return '<div class="hxf-action-stack">' + buttons.join('') + '</div>'; };
    var back = '<button type="button" class="hxf-secondary" data-act="noticeBack">' + fmt('X-cart-back') + '</button>';
    var openCart = '<button type="button" class="hxf-primary" data-act="openCart">' + fmt('X-cart-open') + '</button>';
    if (n.kind === 'unavailable') {
      return heading(fmt('X-cart-unavailable-title')) + '<div class="hxf-reco hxf-notice">' +
        '<p>' + fmt('X-cart-unavailable-text') + '</p>' + noticeList(n.items) + '</div>' +
        stack(['<button type="button" class="hxf-primary" data-act="addRest">' + fmt('X-cart-add-rest') + '</button>', back]);
    }
    var codeLine = n.codeSaved === false && n.code
      ? '<p class="hxf-notice-code">' + fmt('X-cart-code-failed', { code: n.code }) + '</p>' : '';
    if (n.kind === 'partial') {
      return heading(fmt('X-cart-partial-title')) + '<div class="hxf-reco hxf-notice">' +
        (n.failed.length ? '<p>' + fmt('X-cart-partial-text') + '</p>' + noticeList(n.failed.map(function (f) { return f.item; })) : '') +
        (n.message ? '<p class="hxf-small">' + esc(n.message) + '</p>' : '') + codeLine + '</div>' + stack([openCart]);
    }
    return heading(fmt('X-cart-failed-title')) + '<div class="hxf-reco hxf-notice">' +
      (n.failed && n.failed.length ? '<p>' + fmt('X-cart-unavailable-text') + '</p>' + noticeList(n.failed.map(function (f) { return f.item; })) : '') +
      '<p>' + (n.message ? esc(n.message) : fmt('X-cart-failed-text')) + '</p></div>' +
      stack(['<button type="button" class="hxf-primary" data-act="addToCart">' + fmt('X-cart-try-again') + '</button>', back]);
  }

  function screenKey() {
    if (cartNotice) return 'notice:' + cartNotice.kind;
    return state.view + ':' + (state.view === 'question' ? state.current + ':' + engine.currentQuestionId(state) : state.finished || '');
  }

  function render() {
    renderBar();
    if (!els.dialog) return;
    var html = cartNotice ? renderNotice()
      : state.view === 'question' ? renderQuestion()
      : state.view === 'transition' ? renderTransition()
      : state.view === 'selection' ? renderSelection() : renderParts();
    var key = screenKey();
    var moved = key !== lastScreen;
    var focusedId = !moved && document.activeElement && els.body.contains(document.activeElement) ? focusSignature(document.activeElement) : null;
    var scroll = els.body.scrollTop;
    els.body.innerHTML = html;
    if (state.view === 'question' && config.questions[engine.currentQuestionId(state)].type === 'measurements') refreshMeasurements();
    if (moved) {
      els.body.scrollTop = 0;
      if (lastScreen !== null && els.dialog.open) {
        var h = els.body.querySelector('.hxf-h2');
        if (h) h.focus({ preventScroll: true });
      }
    } else {
      els.body.scrollTop = scroll;
      restoreFocus(focusedId);
    }
    lastScreen = key;
  }

  // Keep keyboard focus on the same control after a re-render (e.g. tapping + on a quantity).
  function focusSignature(el) {
    var attrs = ['data-act', 'data-part', 'data-line', 'data-row', 'data-delta', 'id'];
    return attrs.map(function (a) { return el.getAttribute(a) || ''; }).join('|');
  }

  function restoreFocus(sig) {
    if (!sig) return;
    var all = els.body.querySelectorAll('button, input');
    for (var i = 0; i < all.length; i++) {
      if (focusSignature(all[i]) === sig) {
        if (all[i].disabled) {
          var h = els.body.querySelector('.hxf-h2');
          if (h) h.focus({ preventScroll: true });
        } else {
          all[i].focus({ preventScroll: true });
        }
        return;
      }
    }
  }

  function renderBar() {
    var s = engine.summary(state);
    els.barTitle.innerHTML = s.parts ? fmt('DE-102') : fmt('DE-001');
    els.barSub.innerHTML = s.parts ? fmt('DE-101', { n: s.parts, m: s.products }) : fmt('DE-002');
  }

  // ---- Dialog ----------------------------------------------------------------------------
  function openFinder() {
    if (els.dialog.open) return;
    if (slideDone) slideDone();
    if (!cartNotice) analytics.track('finder_open');
    lastScreen = null;
    render();
    els.dialog.showModal();
    document.documentElement.classList.add('hxf-locked');
    requestAnimationFrame(function () { els.dialog.classList.add('is-open'); });
    var h = els.body.querySelector('.hxf-h2');
    if (h) h.focus({ preventScroll: true });
  }

  function closeFinder() {
    if (!els.dialog.open) return;
    if (els.help.open) els.help.close();
    if (!cartBusy) cartNotice = null;
    els.dialog.classList.remove('is-open');
    var done = function () {
      if (els.dialog.classList.contains('is-open')) return;
      els.dialog.close();
      document.documentElement.classList.remove('hxf-locked');
      els.bar.focus({ preventScroll: true });
    };
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) done(); else setTimeout(done, 280);
  }

  // ---- Cart ------------------------------------------------------------------------------
  // Part names in order notes are for staff, so always English ("Front wheel"), whatever the shop language.
  function staffPartName(id) { return plain(String(texts.en[partById(id).name] || id)); }

  // All sending, checking and reporting is in hexlox-finder-cart.js; this part only shows the result.
  function addToCart(skipUnavailable) {
    if (cartBusy || !window.HexloxFinderCart) return;
    cartBusy = true;
    render();
    var sel = engine.selection(state);
    var keys = [config.key_addon.product];
    sel.forEach(function (r) {
      r.lines.forEach(function (l) { keys.push(l.product); });
      r.addons.forEach(function (a) { keys.push(a.product); });
    });
    var code = String(state.hexloxCode || '').trim();
    var sum = engine.summary(state);
    loadProducts(keys).then(function () {
      return window.HexloxFinderCart.add({
        fetch: window.fetch.bind(window),
        root: routesRoot,
        build: function (cart) {
          return engine.buildCart(state, { cartHasKey: engine.cartHasKey(cart), cartAttributes: cart.attributes, partLabel: staffPartName });
        },
        codeAttribute: config.hexlox_code.attribute,
        available: variantAvailable,
        skipUnavailable: !!skipUnavailable
      });
    }).then(function (result) {
      cartBusy = false;
      trackCart(result, sum);
      if (result.status === 'unavailable') {
        cartNotice = { kind: 'unavailable', items: result.unavailable };
      } else if (result.status === 'failed') {
        cartNotice = { kind: 'failed', failed: result.failed, message: result.message };
      } else {
        // Something is in the cart now: start the Finder fresh so the same lines cannot be added twice.
        state = engine.initialState();
        save();
        if (result.status === 'ok') {
          // Keep "Adding to cart …" on screen until the drawer has the new items, then swap in one step.
          cartBusy = true;
          return showCart(true).then(function () {
            cartBusy = false;
            cartNotice = null;
            render();
            closeFinder();
          });
        }
        cartNotice = { kind: 'partial', failed: result.failed, message: result.message, codeSaved: result.codeSaved, code: code };
        showCart(false);
      }
      lastScreen = null;
      if (els.dialog.open) render(); else openFinder();
    }, function (err) {
      if (window.console) console.error('Hexlox Finder: add to cart failed', err);
      trackCart({ status: 'failed', failed: [] }, sum);
      cartBusy = false;
      cartNotice = { kind: 'failed', failed: [], message: '' };
      lastScreen = null;
      if (els.dialog.open) render(); else openFinder();
    });
  }

  // Update the theme's cart drawer and cart icon; open the drawer if asked. Themes without a drawer,
  // and the cart page itself, get a page load instead. Resolves when the drawer is updated.
  function showCart(open) {
    var drawer = document.querySelector('cart-drawer');
    var onCartPage = /\/cart\/?$/.test(window.location.pathname);
    var usable = drawer && typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function';
    if (onCartPage || !usable) {
      if (open && onCartPage) window.location.reload();
      else if (open) window.location.href = routesRoot + 'cart';
      return Promise.resolve();
    }
    var sections = drawer.getSectionsToRender();
    return fetch(window.location.pathname + '?sections=' + sections.map(function (x) { return x.id; }).join(','), { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (json) {
        if (open) {
          drawer.renderContents({ sections: json });
        } else {
          sections.forEach(function (x) {
            var el = x.selector ? document.querySelector(x.selector) : document.getElementById(x.id);
            if (el && json[x.id]) el.innerHTML = drawer.getSectionInnerHTML(json[x.id], x.selector);
          });
        }
        // The drawer was drawn while the cart was empty; without this it keeps hiding the new items
        // (the theme's own Add to cart button does the same).
        drawer.classList.remove('is-empty');
        var inner = drawer.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
      })
      .catch(function () { if (open) window.location.href = routesRoot + 'cart'; });
  }

  function onClick(e) {
    var t = e.target.closest('[data-act]');
    if (!t || !root.contains(t) || t.tagName === 'INPUT') return;
    var a = t.getAttribute('data-act');
    var d = function (name) { return t.getAttribute('data-' + name); };
    if (cartBusy && a !== 'close') return;
    switch (a) {
      case 'open': openFinder(); break;
      case 'close': closeFinder(); break;
      case 'help': openHelp(d('help')); break;
      case 'helpClose': els.help.close(); break;
      case 'togglePart': act({ type: 'togglePart', part: d('part') }); break;
      case 'answer': act({ type: 'answer', question: d('q'), value: d('v') }); break;
      case 'gridQty': act({ type: 'gridQty', row: d('row'), delta: Number(d('delta')) }); break;
      case 'changeQty': act({ type: 'changeQty', part: d('part'), line: d('line'), delta: Number(d('delta')) }); break;
      case 'submit': submit(); break;
      case 'addToCart': addToCart(false); break;
      case 'addRest': addToCart(true); break;
      case 'noticeBack': cartNotice = null; render(); break;
      case 'openCart': cartNotice = null; closeFinder(); showCart(true); break;
      default: act({ type: a });   // start, back, continue, restart, addAnother, showSelection
    }
  }

  function submit() {
    var qid = engine.currentQuestionId(state);
    if (qid && config.questions[qid].type === 'measurements') {
      els.body.querySelectorAll('.hxf-measure-input').forEach(function (i) { i.setAttribute('data-touched', 'true'); });
      refreshMeasurements();
    }
    act({ type: 'submit' });
  }

  function onChange(e) {
    var t = e.target;
    if (t.matches('[data-act="ownInsert"]')) act({ type: 'ownInsert', part: t.getAttribute('data-part'), owned: t.checked });
    else if (t.matches('[data-act="ownKey"]')) act({ type: 'ownKey', owned: t.checked });
  }

  function onInput(e) {
    var t = e.target;
    if (t.matches('[data-field]')) {
      act({ type: 'setDraft', field: t.getAttribute('data-field'), value: t.value }, { render: false });
      refreshMeasurements();
    } else if (t.matches('[data-code]')) {
      act({ type: 'hexloxCode', value: t.value }, { render: false });
    }
  }

  function onFocusOut(e) {
    var t = e.target;
    if (t.matches && t.matches('[data-field]') && t.value.trim()) {
      t.setAttribute('data-touched', 'true');
      refreshMeasurements();
    }
  }

  function onKeyDown(e) {
    var t = e.target;
    if (e.key !== 'Enter' || !t.matches || !t.matches('[data-field]')) return;
    e.preventDefault();
    var inputs = Array.prototype.slice.call(els.body.querySelectorAll('[data-field]'));
    var i = inputs.indexOf(t);
    if (i < inputs.length - 1) inputs[i + 1].focus();
    else {
      var btn = els.body.querySelector('[data-measure-continue]');
      if (btn && !btn.disabled) submit();
      else { t.setAttribute('data-touched', 'true'); refreshMeasurements(); }
    }
  }

  // ---- Build -----------------------------------------------------------------------------
  // The animated bar icon comes inline from the section (assets/hexlox-finder-icon.svg); the PNG is the fallback.
  var iconTpl = root.querySelector('template[data-hxf-icon]');
  var iconSvg = iconTpl ? iconTpl.innerHTML.trim() : '';
  function barIcon() {
    if (iconSvg.indexOf('<svg') !== 0) return img('logo_bar', '', 'hxf-shield-img', '48px', true);
    return iconSvg.replace('<svg', '<svg class="hxf-shield-img"');
  }

  function build() {
    var title = fmt('DE-003');
    root.innerHTML =
      '<button type="button" class="hxf-bar" data-act="open" aria-haspopup="dialog">' +
        '<span class="hxf-bar-main"><span class="hxf-shield">' + barIcon() + '</span>' +
        '<span class="hxf-bar-copy"><span class="hxf-bar-title" data-bar-title></span><span class="hxf-bar-sub" data-bar-sub></span></span>' +
        '<span class="hxf-bar-arrow" aria-hidden="true">↑</span></span></button>' +
      '<dialog class="hxf-dialog" aria-labelledby="hxf-title">' +
        '<div class="hxf-head"><div class="hxf-brand">' + img('logo_header', '', 'hxf-brand-img', '190px', true) +
          '<div class="hxf-title" id="hxf-title">' + title + '</div></div>' +
          '<button type="button" class="hxf-close" data-act="close" aria-label="' + esc(txt('X-close')) + '">×</button></div>' +
        '<div class="hxf-body" data-hxf-body></div>' +
        '<dialog class="hxf-help" aria-labelledby="hxf-help-title">' +
          '<div class="hxf-help-head"><div class="hxf-help-title" id="hxf-help-title"></div>' +
          '<button type="button" class="hxf-help-close" data-act="helpClose" aria-label="' + esc(txt('X-close')) + '">×</button></div>' +
          '<div class="hxf-help-body"></div></dialog>' +
      '</dialog>';
    els.bar = root.querySelector('.hxf-bar');
    els.barTitle = root.querySelector('[data-bar-title]');
    els.barSub = root.querySelector('[data-bar-sub]');
    els.dialog = root.querySelector('.hxf-dialog');
    els.body = root.querySelector('[data-hxf-body]');
    els.help = root.querySelector('.hxf-help');
    els.helpTitle = root.querySelector('.hxf-help-title');
    els.helpBody = root.querySelector('.hxf-help-body');
    els.helpClose = root.querySelector('.hxf-help-close');

    root.addEventListener('click', onClick);
    root.addEventListener('change', onChange);
    root.addEventListener('input', onInput);
    root.addEventListener('keydown', onKeyDown);
    root.addEventListener('focusout', onFocusOut);
    // Esc closes the help pop-up first, then the Finder (with its slide-out).
    els.dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      if (els.help.open) els.help.close(); else closeFinder();
    });
    // Tapping the dark area next to the drawer (desktop) closes it; same for the help pop-up.
    els.dialog.addEventListener('click', function (e) { if (e.target === els.dialog) closeFinder(); });
    els.help.addEventListener('click', function (e) { if (e.target === els.help) els.help.close(); });

    renderBar();
    if (slidesIn()) startSlideIn();
    root.classList.add('is-ready');
    trackBarHeight();
  }

  // ---- Bar slide-in ----------------------------------------------------------------------
  // On the first page of a visit (desktop, tablet and phone), the bar slides up from below the screen
  // 4 seconds after it is ready and in view. Later pages and "reduce motion": shown right away.
  // The visit counts as "seen" once the slide has actually played.
  var SLIDE_DELAY = 4000;
  var SLIDE_TIME = 1000;
  var slideKey = 'hxf-bar-slid';
  var slideTimer = null;
  var slideDone = null;

  function slidesIn() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    var s = storage();
    if (!s) return false;                      // can't tell first page from later ones: show right away
    try { return !s.getItem(slideKey); } catch (e) { return false; }
  }

  function startSlideIn() {
    root.classList.add('hxf-below', 'hxf-wait');
    slideDone = function () { clearTimeout(slideTimer); slideUp(true); };
    whenInView(countDown);
  }

  function countDown() {
    clearTimeout(slideTimer);
    slideTimer = setTimeout(function () { slideUp(false); }, SLIDE_DELAY);
  }

  // Things that sit over the bottom of the screen, where the launcher appears: the cookie banner (Pandectes,
  // until answered) and, in staff previews only, Shopify's preview bar. The slide waits until they are gone.
  var COVERS = ['#pandectes-banner', '#pandectes-banner .cc-window', '#PBarNextFrameWrapper', '#PBarNextFrame',
    '#preview-bar-iframe'];

  function covered() {
    return COVERS.some(function (sel) {
      var el = document.querySelector(sel);
      if (!el) return false;
      var cs = window.getComputedStyle(el);
      var r = el.getBoundingClientRect();
      return cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity) > 0 &&
        r.height > 20 && r.width > 20 && r.top < window.innerHeight - 20 && r.bottom > window.innerHeight - 120;
    });
  }

  // Run fn once the page is in view: not in a background tab, nothing covering the launcher's place.
  function whenInView(fn) {
    if (!document.hidden && !covered()) { fn(); return; }
    clearTimeout(slideTimer);
    slideTimer = setTimeout(function () { whenInView(fn); }, 250);
  }

  function slideUp(now) {
    if (!root.classList.contains('hxf-below')) return;
    if (!now && (document.hidden || covered())) { whenInView(countDown); return; }
    slideDone = null;
    try { storage().setItem(slideKey, '1'); } catch (e) { /* storage blocked */ }
    root.classList.add('hxf-slide');
    void els.bar.offsetWidth;                  // settle the start position so the move is animated
    root.classList.remove('hxf-below');
    // The colour loop and the icon start once the bar has arrived.
    setTimeout(function () { root.classList.remove('hxf-wait', 'hxf-slide'); }, SLIDE_TIME);
  }

  // The bar's height (it changes with the text, language and screen width) goes into --hxf-bar-h,
  // so the CSS can keep the end of the page and the Gorgias chat button clear of the bar.
  function trackBarHeight() {
    var html = document.documentElement;
    var update = function () { html.style.setProperty('--hxf-bar-h', Math.ceil(els.bar.getBoundingClientRect().height) + 'px'); };
    html.classList.add('hxf-has-bar');
    update();
    if (window.ResizeObserver) new ResizeObserver(update).observe(els.bar);
    else window.addEventListener('resize', update);
  }

  function getJson(url) {
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('Hexlox Finder: could not load ' + url + ' (' + r.status + ')');
      return r.json();
    });
  }

  Promise.all([getJson(root.getAttribute('data-config-url')), getJson(root.getAttribute('data-texts-url'))])
    .then(function (data) {
      config = data[0];
      texts = data[1];
      engine = window.HexloxFinderEngine.create(config);
      if (window.HexloxFinderAnalytics) analytics = window.HexloxFinderAnalytics.create({ version: config.finder_version });
      state = load();
      build();
    })
    .catch(function (err) {
      if (window.console) console.error(err);
    });
})();
